import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useShift } from "../Context/ShiftContext";
import { useCart } from "../Context/CartContext";
import { useSnackbar } from "react-simple-snackbar";
import * as api from "../api";

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 12,
    padding: theme.spacing(0, 0, 1),
  },
  title: {
    fontSize: "1rem",
    fontWeight: 600,
    padding: theme.spacing(1.5, 2),
  },
  content: {
    padding: theme.spacing(0, 2, 1),
  },
  lastShiftBadge: {
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 8,
    backgroundColor: theme.palette.grey[100],
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  lastShiftLabel: {
    fontWeight: 600,
    marginBottom: 2,
    color: theme.palette.text.primary,
  },
  hint: {
    fontSize: "0.8125rem",
    marginBottom: theme.spacing(1.5),
  },
  onShiftSection: {
    marginBottom: theme.spacing(1.5),
  },
  onShiftLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  listItem: {
    padding: theme.spacing(0.25, 0),
    minHeight: 36,
  },
  employeeOption: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(0.75, 1),
  },
  employeeName: {
    fontWeight: 500,
    fontSize: "0.875rem",
  },
  employeeInactive: {
    color: theme.palette.text.disabled,
    fontWeight: 400,
  },
  lastShiftText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  lastShiftPrefix: {
    color: theme.palette.text.secondary,
    marginRight: 4,
  },
  actions: {
    padding: theme.spacing(1, 2),
  },
}));

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatLastShift = (startTime, endTime) => {
  if (!startTime || !endTime) return "";
  const datePart = new Date(startTime).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  const start = formatTime(startTime);
  const end = formatTime(endTime);
  return `${datePart} ${start} – ${end}`;
};

export default function ShiftStartDialog() {
  const classes = useStyles();
  const snackbarOptions = { position: "top-center", style: { zIndex: 99999 } };
  const [openSnackbar] = useSnackbar(snackbarOptions);
  const [employeesList, setEmployeesList] = useState([]);
  const [lastShifts, setLastShifts] = useState({});
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const {
    showShiftDialog,
    closeShiftDialog,
    activeShiftsList,
    fetchActiveShifts,
    pendingAddToCart,
    startShift,
    shiftLoading,
  } = useShift();
  const { handleAddToCart } = useCart();

  useEffect(() => {
    if (showShiftDialog) {
      fetchActiveShifts();
      setLoadingEmployees(true);
      setSelectedEmployee("");
      Promise.all([api.fetchEmployees(), api.getLastShiftByEmployee()])
        .then(([empRes, shiftRes]) => {
          const employees = Array.isArray(empRes.data) ? empRes.data : [];
          setLastShifts(shiftRes.data || {});
          setEmployeesList(employees);
        })
        .catch(() => {
          setEmployeesList([]);
          setLastShifts({});
        })
        .finally(() => setLoadingEmployees(false));
    }
  }, [showShiftDialog, fetchActiveShifts]);

  const handleStartShift = async () => {
    if (!selectedEmployee) {
      openSnackbar("Please select an employee");
      return;
    }
    const employee = employeesList.find((e) => e._id === selectedEmployee);
    if (!employee) {
      openSnackbar("Selected employee not found");
      return;
    }
    if (!employee.active) {
      openSnackbar("Cannot start shift for inactive employee");
      return;
    }
    const shift = await startShift(employee, openSnackbar);
    if (shift) {
      if (pendingAddToCart?.item) {
        handleAddToCart(pendingAddToCart.item, pendingAddToCart.quantity || 1);
      }
      closeShiftDialog();
    }
  };

  const lastShiftEntries = Object.entries(lastShifts);
  const mostRecentShift = lastShiftEntries.length
    ? lastShiftEntries.reduce((best, [empId, sh]) => {
        const end = sh?.endTime ? new Date(sh.endTime) : null;
        if (!end) return best;
        if (!best) return { empId, ...sh };
        return end > new Date(best.endTime) ? { empId, ...sh } : best;
      }, null)
    : null;
  const mostRecentEmpName = mostRecentShift
    ? employeesList.find((e) => e._id === mostRecentShift.empId)?.name || "Unknown"
    : null;

  return (
    <Dialog
      open={showShiftDialog}
      onClose={closeShiftDialog}
      maxWidth="xs"
      fullWidth
      style={{ zIndex: 10000 }}
      PaperProps={{ className: classes.paper, style: { zIndex: 10000 } }}
    >
      <DialogTitle className={classes.title}>Start Shift</DialogTitle>
      <DialogContent className={classes.content}>
        <Typography variant="body2" color="textSecondary" className={classes.hint}>
          Select employee to start shift.
        </Typography>
        {mostRecentEmpName && mostRecentShift && (
          <Box className={classes.lastShiftBadge}>
            <div className={classes.lastShiftLabel}>Last shift</div>
            <div>
              {mostRecentEmpName} → {formatLastShift(mostRecentShift.startTime, mostRecentShift.endTime)}
            </div>
          </Box>
        )}
        {activeShiftsList.length > 0 && (
          <Box className={classes.onShiftSection}>
            <Typography className={classes.onShiftLabel}>On shift now</Typography>
            <List dense disablePadding>
              {activeShiftsList.map((s) => (
                <ListItem key={s._id} disableGutters className={classes.listItem}>
                  <ListItemText
                    primary={s.employeeName || s.userName}
                    secondary={formatTime(s.startTime)}
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
        <Box>
          {loadingEmployees ? (
            <Box display="flex" justifyContent="center" py={1.5}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="shift-start-employee-label">Employee</InputLabel>
              <Select
                labelId="shift-start-employee-label"
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                label="Employee"
                renderValue={(id) => {
                  const emp = employeesList.find((e) => e._id === id);
                  return emp ? `${emp.name}${!emp.active ? " (Inactive)" : ""}` : "";
                }}
                MenuProps={{
                  style: { zIndex: 10001 },
                  PaperProps: {
                    style: { zIndex: 10001, maxHeight: 320 },
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select</em>
                </MenuItem>
                {[...employeesList]
                  .sort((a, b) => {
                    const aEnd = lastShifts[a._id]?.endTime;
                    const bEnd = lastShifts[b._id]?.endTime;
                    if (!aEnd && !bEnd) return 0;
                    if (!aEnd) return 1;
                    if (!bEnd) return -1;
                    return new Date(bEnd) - new Date(aEnd);
                  })
                  .map((emp) => {
                    const last = lastShifts[emp._id];
                    const lastShiftStr = last
                      ? formatLastShift(last.startTime, last.endTime)
                      : "No previous shift";
                    return (
                      <MenuItem
                        key={emp._id}
                        value={emp._id}
                        disabled={!emp.active}
                        className={classes.employeeOption}
                      >
                        <div style={{ width: "100%" }}>
                          <span
                            className={`${classes.employeeName} ${!emp.active ? classes.employeeInactive : ""}`}
                          >
                            {emp.name}
                            {!emp.active && " (Inactive)"}
                          </span>
                          <div className={classes.lastShiftText}>
                            <span className={classes.lastShiftPrefix}>Last shift →</span>
                            {lastShiftStr}
                          </div>
                        </div>
                      </MenuItem>
                    );
                  })}
              </Select>
            </FormControl>
          )}
          {!loadingEmployees && employeesList.length === 0 && (
            <Typography variant="caption" color="textSecondary" style={{ marginTop: 8, display: "block" }}>
              No employees. Add in Employee Master.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button onClick={closeShiftDialog} size="small">
          Cancel
        </Button>
        <Button
          onClick={handleStartShift}
          color="primary"
          variant="contained"
          size="small"
          disabled={
            shiftLoading ||
            !selectedEmployee ||
            loadingEmployees ||
            !employeesList.find((e) => e._id === selectedEmployee)?.active
          }
        >
          {shiftLoading ? "Starting…" : "Start"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
